import { CreateApolloClient } from '@/api/client/apollo';
import { InitialalsAvatar } from '@/components/displays/InitialsAvatar';
import { capture } from '@/handlers/error';
import { useSchool, useSession } from '@/providers/session/SessionProvider';
import { generateAvatarColor, getInitials } from '@/utils/avatar';
import EditIcon from '@mui/icons-material/Edit';
import GroupsIcon from '@mui/icons-material/Groups';
import { Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid, getGridStringOperators, GridActionsCellItem, GridColDef, GridFilterModel, GridPaginationModel, GridRowId, GridSortModel, useGridApiRef } from '@mui/x-data-grid';
import { useNotifications } from '@toolpad/core/useNotifications';
import * as React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ClassData } from '../../types/class';
import { ClassCreateDialog } from './components/ClassCreateDialog';
import { ClassEmptyView } from './components/ClassEmptyView';
import { ClassPageHeader } from './components/ClassPageHeader';
import { parseFilterModel } from './filter';
import { CREATE_CLASS, CreateClassResponse, GET_CLASSES, GetClassesResponse, UPDATE_CLASS, UpdateClassResponse } from './gql';

const defaultSortModel: { field: string; sort: 'asc' | 'desc' } = { field: 'className', sort: 'asc' };

export default function ClassesPage() {
    // provider
    const { t } = useTranslation();
    const { session } = useSession();
    const { school } = useSchool();
    const notifications = useNotifications();
    const navigate = useNavigate();

    // ui
    const [loading, setLoading] = React.useState(true);
    const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
    const [editPayload, setEditPayload] = React.useState<Pick<ClassData, 'id' | 'className' | 'schoolYear'> | null>(null);

    const [data, setData] = React.useState<{ count: number; rows: ClassData[] } | undefined>(undefined);
    const [queryOption, setQueryOption] = React.useState<{ pagination?: GridPaginationModel; sort?: GridSortModel; filter?: GridFilterModel } | undefined>(undefined);
    const gridApiRef = useGridApiRef();

    const onCreateClass = () => {
        setCreateDialogOpen(true);
    };

    const handleCreateClassClose = async (data: Pick<ClassData, 'className' | 'schoolYear'> | null) => {
        if (!data || !school?.id || !session?.token) {
            setCreateDialogOpen(false);
            return;
        }

        const client = CreateApolloClient(session.token);

        try {
            await client.mutate<CreateClassResponse>({
                mutation: CREATE_CLASS,
                variables: {
                    data: {
                        className: data.className,
                        schoolYear: data.schoolYear,
                        school: {
                            connect: {
                                id: school.id,
                            },
                        },
                    },
                },
            });

            setCreateDialogOpen(false);
            refresh();
        } catch (error) {
            notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
            capture(error);
        }
    };

    const onEditClass = React.useCallback(
        (rowId: GridRowId) => () => {
            const record = data?.rows.find((r) => r.id === rowId);
            if (record) {
                setEditPayload({ id: record.id, className: record.className, schoolYear: record.schoolYear });
            }
        },
        [data],
    );

    const onViewStudents = React.useCallback(
        (rowId: GridRowId) => {
            const record = data?.rows.find((r) => r.id === rowId);
            if (record) {
                navigate(`/students?className=${encodeURIComponent(`${record.className} (${record.schoolYear})`)}`);
            }
        },
        [data, navigate],
    );

    const handleEditClassClose = async (data: Pick<ClassData, 'className' | 'schoolYear'> | null) => {
        if (!session?.token) {
            setData(undefined);
            return;
        }

        if (data && editPayload) {
            const client = CreateApolloClient(session.token);
            try {
                await client.mutate<UpdateClassResponse>({
                    mutation: UPDATE_CLASS,
                    variables: {
                        where: { id: editPayload.id },
                        data: {
                            className: data.className,
                            schoolYear: data.schoolYear,
                        },
                    },
                });
                refresh();
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        }
        setEditPayload(null);
    };

    // data grid
    const columns: GridColDef[] = React.useMemo(
        () => [
            {
                field: 'className',
                headerName: t('class.table.name'),
                sortable: true,
                filterOperators: getGridStringOperators().filter((operator) => operator.value === 'contains'),
                minWidth: 200,
                flex: 1,
                renderCell: (params) => {
                    const name = params.value || '';
                    const avatarColor = generateAvatarColor(name);
                    const bg = avatarColor?.bg;
                    const text = avatarColor?.text;

                    return (
                        <Stack direction='row' spacing={1} alignItems='center' height='100%'>
                            <InitialalsAvatar
                                sx={{
                                    bgcolor: bg,
                                    color: text,
                                }}
                            >
                                {getInitials(name)}
                            </InitialalsAvatar>
                            <Typography variant='body2'>{name}</Typography>
                        </Stack>
                    );
                },
            },
            {
                field: 'studentsCount',
                headerName: t('class.table.studentsCount'),
                sortable: false,
                filterable: false,
                minWidth: 120,
            },
            {
                field: 'schoolYear',
                headerName: t('class.table.schoolYear'),
                sortable: false,
                filterable: false,
                minWidth: 160,
            },
            {
                field: 'actions',
                type: 'actions',
                headerName: t('common.actions'),
                cellClassName: 'actions',
                getActions: ({ id }) => [
                    <GridActionsCellItem
                        icon={
                            <Tooltip title={t('class.actions.editClass')}>
                                <EditIcon />
                            </Tooltip>
                        }
                        label='Edit'
                        className='textPrimary'
                        onClick={onEditClass(id)}
                        color='inherit'
                    />,
                    <GridActionsCellItem
                        icon={
                            <Tooltip title={t('class.actions.viewStudents')}>
                                <GroupsIcon />
                            </Tooltip>
                        }
                        label='View Students'
                        className='textPrimary'
                        // Pass the row id to onViewStudents
                        onClick={() => onViewStudents(id)}
                        color='inherit'
                    />,
                ],
            },
        ],
        [onEditClass, onViewStudents, t],
    );

    const refresh = React.useCallback(async () => {
        if (!queryOption?.pagination || !school?.id || !session?.token) {
            setData(undefined);
            return;
        }

        const client = CreateApolloClient(session?.token);

        setLoading(true);

        const filters = queryOption.filter ? parseFilterModel(queryOption.filter) : [];

        await client
            .query<GetClassesResponse>({
                query: GET_CLASSES,
                fetchPolicy: 'network-only',
                variables: {
                    skip: queryOption.pagination.page * queryOption.pagination.pageSize,
                    take: queryOption.pagination.pageSize,
                    orderBy: queryOption.sort?.[0] ? [{ [queryOption.sort[0].field]: queryOption.sort[0].sort }] : [{ [defaultSortModel.field]: defaultSortModel.sort }],
                    where: {
                        AND: [{ school: { id: { equals: school?.id } } }, ...filters],
                    },
                },
            })
            .then((response) => {
                ReactDOM.flushSync(() => {
                    setData({
                        count: response.data.classesCount,
                        rows: response.data.classes.map((classData) => ({
                            id: classData.id,
                            className: classData.className,
                            studentsCount: classData.studentsCount,
                            schoolYear: classData.schoolYear,
                        })),
                    });
                });
            })
            .catch((error) => {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                capture(error);
            });
        setLoading(false);
    }, [notifications, queryOption, school?.id, session?.token]);

    /**
     * refresh data when
     *   - pagination model changed
     *   - sort model changed
     *   - school changed
     *   - session changed (not likely)
     */
    React.useEffect(() => {
        refresh();
    }, [refresh]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', padding: 16 }}>
            <Stack sx={{ flex: 1, overflow: 'hidden' }} spacing={2}>
                <ClassPageHeader onAdd={onCreateClass} />
                <DataGrid
                    loading={loading}
                    columns={columns}
                    autoPageSize
                    filterMode='server'
                    sortingMode='server'
                    paginationMode='server'
                    initialState={{
                        sorting: { sortModel: [defaultSortModel] },
                    }}
                    onPaginationModelChange={(model) => {
                        setQueryOption((prev) => (prev ? { ...prev, pagination: model } : { pagination: model }));
                    }}
                    onSortModelChange={(model) => {
                        setQueryOption((prev) => (prev ? { ...prev, sort: model } : { sort: model }));
                    }}
                    onFilterModelChange={(model) => {
                        setQueryOption((prev) => (prev ? { ...prev, filter: model } : { filter: model }));
                    }}
                    rows={data?.rows}
                    rowCount={data?.count || 0}
                    slots={{
                        noRowsOverlay: () => <ClassEmptyView onAdd={onCreateClass} />,
                    }}
                    slotProps={{
                        loadingOverlay: {
                            variant: 'linear-progress',
                            noRowsVariant: 'linear-progress',
                        },
                    }}
                    hideFooterSelectedRowCount
                    apiRef={gridApiRef}
                />
            </Stack>

            {createDialogOpen && <ClassCreateDialog open={createDialogOpen} onClose={handleCreateClassClose} payload={null} />}

            {editPayload && <ClassCreateDialog open={!!editPayload} onClose={handleEditClassClose} payload={{ data: editPayload }} />}
        </div>
    );
}
